// The DFT module's protocol and library choice (fast / balanced / precise; SSSP, PseudoDojo, ...: the server sets the
// pseudopotentials, cutoffs, k-mesh, smearing and SCF accuracy of the recipe), and the project's own UPF files
// (uploaded like any file, checked by the server, then offered in the pseudopotential table as "your file").
import { ActionIcon, Alert, Anchor, Badge, Button, FileButton, Group, List, SegmentedControl, Select, Stack, Table, Text,
  Tooltip } from "@mantine/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ErrorBox } from "../../components/Errors";
import { del, formatBytes, uploadFile } from "../../lib/api";
import { projectPseudos, qeProtocol, recipeProtocol, USER_PSEUDO_MAX_BYTES, type ProtocolId, type QeStatus,
  type Recipe } from "../../lib/qe";

const PROTOCOL_ORDER: ProtocolId[] = ["fast", "balanced", "precise"];

/** Fast / Balanced / Precise and the library: a change asks the server to apply them to the recipe. */
export function ProtocolBar({ recipe, status, onChange }: {
  recipe: Recipe; status: QeStatus | undefined; onChange: (r: Recipe) => void;
}) {
  const current = recipeProtocol(recipe);
  const protocols = status?.protocols ?? [];
  const libraries = status?.libraries ?? [];
  const [notes, setNotes] = useState<string[]>([]);
  const apply = useMutation({
    mutationFn: (p: { name: ProtocolId; library: string | null }) => qeProtocol(recipe, p.name, p.library),
    onSuccess: (r) => { setNotes(r.notes); onChange(r.recipe); },
  });
  if (!protocols.length) return null;
  const name = current?.name ?? null;
  const library = current?.library ?? null;
  const about = protocols.find((p) => p.id === (apply.variables?.name ?? name))?.about;
  // a library the user chose stays with another protocol; the protocol's own (SSSP efficiency / precision) follows it
  const ownLibrary = library && library !== protocols.find((p) => p.id === name)?.library ? library : null;
  const libInfo = libraries.find((l) => l.name === library);
  return (
    <Stack gap={4} data-testid="qe-protocol">
      <Group gap="xs" align="flex-end" wrap="wrap">
        <Stack gap={2}>
          <Text size="xs" fw={500}>Protocol</Text>
          <SegmentedControl size="xs" value={name ?? ""} aria-label="Protocol" disabled={apply.isPending}
            data={PROTOCOL_ORDER.filter((id) => protocols.some((p) => p.id === id))
              .map((id) => ({ value: id, label: protocols.find((p) => p.id === id)!.label }))}
            onChange={(v) => apply.mutate({ name: v as ProtocolId, library: ownLibrary })} />
        </Stack>
        {libraries.length > 0 && (
          <Select size="xs" w={300} label="Library" value={library} allowDeselect={false} disabled={apply.isPending}
            placeholder={name ? "the protocol's" : "choose a protocol first"}
            data={libraries.map((l) => ({ value: l.name, label: `${l.name}${l.relativistic === "full" ? " (spin-orbit)" : ""}` }))}
            onChange={(v) => v && apply.mutate({ name: (name ?? "balanced") as ProtocolId, library: v })} />
        )}
      </Group>
      <Text size="xs" c="dimmed">
        {name ? `${about ?? ""}.` : "Choose a protocol to set the pseudopotentials, cutoffs, k-mesh, smearing and SCF accuracy together (the recipe's own values until then)."}
        {libInfo && <> {libInfo.description} {libInfo.url && <Anchor href={libInfo.url} target="_blank" rel="noreferrer" size="xs">About</Anchor>}
          {libInfo.citation && <> · cite: {libInfo.citation}</>}</>}
      </Text>
      <ErrorBox error={apply.error} />
      {notes.length > 0 && (
        <Alert color="blue" p="xs" data-testid="qe-protocol-notes">
          <List size="xs" spacing={2}>{notes.map((n) => <List.Item key={n}>{n}</List.Item>)}</List>
        </Alert>
      )}
    </Stack>
  );
}

/** The project's own UPF files: upload, what each is (or why it cannot be used), delete. */
export function UserPseudos({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["dft-user-pseudos", projectId], queryFn: () => projectPseudos(projectId) });
  const [error, setError] = useState<unknown>(null);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["dft-user-pseudos", projectId] });
    void qc.invalidateQueries({ queryKey: ["qe-pseudos"] });
    void qc.invalidateQueries({ queryKey: ["qe-check"] });
    void qc.invalidateQueries({ queryKey: ["project", projectId] });
  };
  const add = useMutation({
    mutationFn: async (f: File) => {
      if (!/^[A-Za-z0-9][A-Za-z0-9_.+-]{0,127}\.upf$/i.test(f.name))
        throw new Error(`${f.name}: a pseudopotential's name is letters, digits, . _ - + and ends in .upf`);
      if (f.size > USER_PSEUDO_MAX_BYTES) throw new Error(`${f.name}: ${formatBytes(f.size)} - at most 10 MB`);
      return uploadFile(projectId, f);
    },
    onSuccess: () => { setError(null); refresh(); },
    onError: setError,
  });
  const remove = useMutation({ mutationFn: (fileId: string) => del(`/files/${fileId}`), onSuccess: refresh, onError: setError });
  const rows = list.data ?? [];
  return (
    <Stack gap={4} data-testid="user-pseudos">
      <Group gap="xs">
        <Text size="xs" fw={500}>Your own pseudopotentials</Text>
        <FileButton onChange={(f) => f && add.mutate(f)} accept=".upf,.UPF">
          {(props) => <Button size="compact-xs" variant="default" {...props} loading={add.isPending}>Add a UPF file…</Button>}
        </FileButton>
      </Group>
      {rows.length === 0 ? (
        <Text size="xs" c="dimmed">UPF files you add here (at most 10 MB each) are checked by the server and offered in the table
          above as "your file", for this project only.</Text>
      ) : (
        <Table fz="xs" verticalSpacing={2} withRowBorders={false}>
          <Table.Tbody>
            {rows.map((r) => (
              <Table.Tr key={r.file_id}>
                <Table.Td>{r.name}</Table.Td>
                <Table.Td>
                  {r.problem
                    ? <Text size="xs" c="red">{r.problem}</Text>
                    : <Text size="xs">{r.element} · {r.pseudo_type} · {r.relativistic || "?"}-relativistic · {r.functional}
                        {r.z_valence != null ? ` · Z = ${r.z_valence}` : ""} · {r.orbitals || "no wavefunctions"}</Text>}
                </Table.Td>
                <Table.Td>{!r.problem && <Badge size="xs" variant="light" color="teal">usable</Badge>}</Table.Td>
                <Table.Td>
                  <Tooltip label="Delete this file"><ActionIcon size="sm" variant="subtle" color="red" aria-label={`delete ${r.name}`}
                    loading={remove.isPending && remove.variables === r.file_id} onClick={() => remove.mutate(r.file_id)}>×</ActionIcon></Tooltip>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
      <ErrorBox error={error ?? list.error} />
    </Stack>
  );
}
